import { useDisclosure } from '@chakra-ui/react'
import {
  Avatar,
  Button,
  ChevronDownIcon,
  DebouncedTextField,
  Flex,
  IconButton,
  MagnifyingGlassIcon,
  Menu,
  PencilSquareIcon,
  PlusIcon,
  ShareIcon,
  Spacer,
  StarIcon,
  Table,
  TableColumn,
  TableProps,
  Text,
  Tooltip,
  toaster,
} from 'components'
import { useWorkspace } from 'components/providers'
import { SortDirection } from 'ka-table'
import { startCase } from 'lodash-es'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PermissionValue, User, WorkspaceUser } from 'types'
import {
  BaseUrl,
  makeUser,
  updateDoc,
  useBlockResourceCreation,
  useBreakpoint,
  useMutateUsers,
  useTheme,
  useUsers,
} from 'utils'
import { defaultProps, makeTypeByField } from 'utils/tableUtils'
import NewUserForm from './NewUserForm'
import { WorkspaceUserDrawer } from './WorkspaceUserDrawer'

export default function WorkspaceUsers() {
  const { status, updateWorkspace, workspace } = useWorkspace()
  const blockUserCreation = useBlockResourceCreation('users')
  const [permissionFilter, setPermissionFilter] = useState<Permission>('all')
  const [search, setSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string>()
  const [statusFilter, setStatusFilter] = useState<Status>('all')
  const [tableProps, setTableProps] = useState(userTableProps)
  const { theme: colorMode } = useTheme()
  const newUserRef = useRef<HTMLInputElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const isMobile = useBreakpoint() < 2
  const { userById, pendingUserByEmail } = workspace || {}
  const [openDrawer, setOpenDrawer] = useState(false)
  const { open, onOpen, onClose } = useDisclosure()

  const userIds = Object.keys(userById || {}).filter(Boolean)
  const { data: activeUsers, loading } = useUsers(userIds)
  const { updateUser, removeUser } = useMutateUsers()
  const users = useMemo(() => {
    const pendingUsers = Object.values(pendingUserByEmail || {})
      .map(u => makeUser(u, workspace))
      .filter(Boolean) as User[]
    return [...pendingUsers, ...activeUsers]
  }, [activeUsers, pendingUserByEmail])
  const selectedUser = useMemo(
    () => users.find(u => u?.id === selectedUserId),
    [users, selectedUserId]
  )

  async function updatePermission(permission: PermissionValue) {
    if (!selectedUser) return

    const email = selectedUser.email
    const user = users.find(u => u?.email === email)
    if (!user) return

    if (user.id) {
      await updateWorkspace({
        userById: {
          ...userById!,
          [user.id]: { ...userById?.[user.id]!, permission },
        },
      })
    } else {
      await updateWorkspace({
        pendingUserByEmail: {
          ...pendingUserByEmail!,
          [user.email]: { ...pendingUserByEmail?.[email], permission } as WorkspaceUser,
        },
      })
    }
  }

  async function handleUpdateUser(body: Partial<User>) {
    if (!selectedUser) return

    const user = users.find(u => u?.email === selectedUser.email)
    if (!user) return

    await updateDoc(user.id, 'users', { id: user.id, ...body })
    updateUser(user.id, body)
  }

  async function handleRemove() {
    if (!selectedUser) return

    const user = users.find(u => u?.email === selectedUser.email)
    if (!user) return

    if (user.id) {
      await updateWorkspace({
        userById: Object.fromEntries(Object.entries(userById!).filter(([id]) => id !== user.id)),
      })
    } else {
      await updateWorkspace({
        pendingUserByEmail: Object.fromEntries(
          Object.entries(pendingUserByEmail!).filter(([email]) => email !== user.email)
        ),
      })
    }
    removeUser(user.id)
    setSelectedUserId(undefined)
  }

  function handleOpenDrawer(user: User) {
    setSelectedUserId(user.id)
    setOpenDrawer(true)
  }

  useEffect(() => {
    if (!users) return
    if (!users.length) return setTableProps(s => ({ ...s, data: [] }))

    const filteredUsers = users.filter(user => {
      if (!user) return false

      const permissionMatches =
        permissionFilter !== 'all' ? user.permission === permissionFilter : true
      const statusMatches = statusFilter !== 'all' ? user.status === statusFilter : true
      return permissionMatches && statusMatches
    }) as User[]

    const generatedCols = makeColumns(filteredUsers)
    const sortedColumn = tableProps.columns.find(c => c.sortDirection)
    const columns = generatedCols.map(c => {
      const defaultSort = c.key === 'email' ? SortDirection.Ascend : undefined
      const sortDirection = c.key === sortedColumn?.key ? sortedColumn.sortDirection : defaultSort
      return { ...c, sortDirection }
    })

    setTableProps(s => ({ ...s, columns, data: filteredUsers }))
  }, [users, permissionFilter, statusFilter])

  function handleNewUser() {
    const blocked = blockUserCreation()
    if (!blocked) onOpen()
  }

  function handleLinkCopy() {
    if (!selectedUser) return

    const email = selectedUser.email
    const inviteLink = `${BaseUrl.web}/invite/${workspace?.id}?email=${email}`

    navigator.clipboard
      .writeText(inviteLink)
      .then(() => {
        toaster.create({ type: 'success', description: 'Invite link copied to clipboard!' })
      })
      .catch(() => {
        toaster.create({ type: 'error', description: 'Error copying invite link' })
      })
  }

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
      overflowY="auto"
      overflowX="hidden"
    >
      <Text textStyle="h3" pt={2}>
        Workspace Members
      </Text>
      <Flex direction="column" gap={2} overflow="hidden">
        <Flex justify="space-between" ref={ref} h="44px">
          <DebouncedTextField
            disabled={loading || !users.length}
            onChange={setSearch}
            placeholder="Search..."
            value={search}
            width={'100%'}
            leftElement={
              <MagnifyingGlassIcon
                color={colorMode === 'light' ? 'rgba(23, 23, 23, 0.6)' : 'white'}
              />
            }
            maxW="250px"
            controlled
            boxShadow={'0px 1px 4px 0px rgba(0, 0, 0, 0.06)'}
            backdropFilter={'blur(14px)'}
            bg={colorMode === 'light' ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.14)'}
            borderRadius={'10px'}
            borderWidth={0}
            pl={10}
          />
          <Spacer />
          <Flex gap={3} ml={3}>
            {!isMobile && (
              <>
                <Menu
                  disabled={loading || !users.length}
                  variant="solid"
                  rightIcon={<ChevronDownIcon fontSize={'md'} />}
                  gap={{ base: 0, sm: 2 }}
                  borderRadius={'10px'}
                  buttonLabel={
                    <Text textStyle={'bodySmall'} fontSize={{ base: '0', sm: 'sm' }}>
                      Status: {startCase(statusFilter) || 'All'}
                    </Text>
                  }
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={statuses.map(value => ({ value }))}
                />
                <Menu
                  disabled={loading || !users.length}
                  variant="solid"
                  rightIcon={<ChevronDownIcon fontSize={'md'} />}
                  gap={{ base: 0, sm: 2 }}
                  borderRadius={'10px'}
                  buttonLabel={
                    <Text textStyle={'bodySmall'} fontSize={{ base: '0', sm: 'sm' }}>
                      Permission: {startCase(permissionFilter) || 'All'}
                    </Text>
                  }
                  value={permissionFilter}
                  onChange={setPermissionFilter}
                  options={permissions.map(value => ({ value }))}
                />
              </>
            )}

            <Button
              palette="primary"
              disabled={status !== 'success'}
              onClick={handleNewUser}
              leftIcon={<PlusIcon />}
            >
              <Text textStyle={'bodySmall'} hideBelow="sm">
                Invite New Member
              </Text>
              <NewUserForm
                ref={newUserRef}
                onComplete={() => onClose()}
                open={open}
                onClose={onClose}
                existingUsers={users.map(u => u?.email ?? '')}
              />
            </Button>
          </Flex>
        </Flex>

        <Flex bg="white" borderRadius="14px" overflowY="auto">
          <Table
            {...tableProps}
            onRowClick={
              isMobile
                ? id => handleOpenDrawer(tableProps!.data?.find(r => r.id === id))
                : undefined
            }
            emptyText="No members found"
            onChange={setTableProps}
            searchText={search}
          />
        </Flex>

        <WorkspaceUserDrawer
          copyInviteLink={handleLinkCopy}
          member={selectedUser}
          onMemberUpdated={handleUpdateUser}
          openDrawer={openDrawer}
          updatePermissions={updatePermission}
          onRemoveMember={handleRemove}
          onDone={() => setOpenDrawer(false)}
        />
      </Flex>
    </Flex>
  )

  function makeColumns(users: Partial<User>[]): TableProps['columns'] {
    if (!users.length) return []

    const userCols = makeTypeByField(users)
    const defCols = (isMobile ? ['email'] : ['email', 'status', 'created', 'permission']).map(
      key => [key, userCols[key]] as const
    )

    return defCols.map(([key, value]) => ({
      isEditable: false,
      key,
      dataType: value,
      title: getColumnTitle(key),
      width: getWidth(key),
      renderCell: getRenderCell(key),
      isResizable: true,
    }))
  }

  function getRenderCell(key: string): TableColumn['renderCell'] {
    switch (key) {
      case 'email':
        return props => {
          const email = props.value
          const name = props.rowData['name']
          const photoUrl = props.rowData['photoUrl']
          return (
            <Flex gap={2} align="center">
              <Avatar name={name} src={photoUrl} size="sm" />
              <Flex direction="column" justify="center">
                {name && <Text textStyle="bodySmall">{name}</Text>}
                <Text
                  textStyle="bodySmall"
                  fontWeight="500"
                  color={name || props.rowData['status'] === 'pending' ? 'gray.500' : 'black'}
                >
                  {email}
                </Text>
              </Flex>
            </Flex>
          )
        }

      case 'status':
        return props => {
          return (
            <Flex gap={3}>
              <Text
                padding="10px 18px"
                bg={props.value === 'pending' ? 'rgba(23, 23, 23, 0.10)' : 'rgba(15, 186, 0, 0.14)'}
                borderRadius="50px"
                color={props.value === 'pending' ? 'rgba(23, 23, 23, 1)' : 'rgba(8, 103, 0, 1)'}
              >
                {startCase(props.value)}
              </Text>
              {props.value === 'pending' && (
                <Tooltip content="Copy invite link">
                  <IconButton
                    aria-label="share the signup link"
                    icon={<ShareIcon fontSize="sm" color="black" />}
                    variant="ghost"
                    onClick={handleLinkCopy}
                  />
                </Tooltip>
              )}
            </Flex>
          )
        }

      case 'permission':
        return props => (
          <Flex flex={1} gap={1} align={'center'}>
            {props.value === 'admin' && <StarIcon fontSize="sm" />}
            <Text>{startCase(props.value)}</Text>
            <Spacer />
            <Tooltip positioning={{ placement: 'top' }} content="Manage Member">
              <IconButton
                icon={
                  <PencilSquareIcon
                    fontSize="sm"
                    color={'rgba(0, 0, 0, 0.5)'}
                    _hover={{
                      color: 'rgba(0, 0, 0, 1)',
                    }}
                  />
                }
                aria-label={'Edit Member'}
                variant="ghost"
                onClick={() => handleOpenDrawer(props.rowData)}
              />
            </Tooltip>
          </Flex>
        )
    }
  }
}

type Permission = 'all' | 'admin' | 'editor' | 'viewer'
const permissions: Permission[] = ['all', 'admin', 'editor', 'viewer']

type Status = 'all' | 'active' | 'pending'
const statuses: Status[] = ['all', 'active', 'pending']

function getColumnTitle(key: string) {
  switch (key) {
    case 'permission':
      return 'Permission Level'
    case 'email':
      return 'Member'
    case 'created':
      return 'Date Added'
    default:
      return startCase(key)
  }
}

function getWidth(key: string) {
  switch (key) {
    case 'created':
      return 140
    case 'member':
      return 140
    case 'permissionLevel':
      return 120
    case 'status':
      return 100
    default:
      return 175
  }
}

const userTableProps: TableProps = {
  ...defaultProps,
  rowKeyField: 'email',
}
